import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  TextField, 
  Button, 
  Card, 
  CardContent, 
  Typography, 
  Box,
  Alert,
  Container,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authenticatedFetch } from '../../utils/api';
import { getAvailableRoles, getRoleDisplayName, Role } from '../../utils/permissions';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const AVAILABLE_ROLES = getAvailableRoles();

interface Company {
  id: number;
  name: string;
}

const userSchema = z.object({
  email: z.string().email('Must be a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  password_confirmation: z.string().min(6, 'Password confirmation must be at least 6 characters'),
  roles: z.array(z.string()).min(1, 'User must have at least one role'),
  company_id: z.number().nullable().optional()
}).refine((data) => data.password === data.password_confirmation, {
  message: "Passwords don't match",
  path: ["password_confirmation"],
}).refine((data) => {
  // If company_rep is selected (and no admin/employee), company_id is required
  const isCompanyRepOnly = data.roles.includes('company_rep') && 
    !data.roles.includes('admin') && 
    !data.roles.includes('employee');
  if (isCompanyRepOnly && !data.company_id) {
    return false;
  }
  return true;
}, {
  message: "Company is required for Company Representative role",
  path: ["company_id"],
});

type UserFormData = z.infer<typeof userSchema>;

const CreateUser = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string>('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  
  const { 
    control, 
    handleSubmit, 
    watch,
    formState: { errors, isSubmitting }
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      email: '',
      password: '',
      password_confirmation: '',
      roles: ['admin'],
      company_id: null
    }
  });

  const selectedRoles = watch('roles');
  const isCompanyRepSelected = selectedRoles?.includes('company_rep') && 
    !selectedRoles?.includes('admin') && 
    !selectedRoles?.includes('employee');

  useEffect(() => {
    if (isCompanyRepSelected) {
      fetchCompanies();
    }
  }, [isCompanyRepSelected]);

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await authenticatedFetch(`${API_URL}/companies?per_page=1000&minimal=true`);
      if (!response.ok) {
        throw new Error('Failed to fetch companies');
      }
      const data = await response.json();
      setCompanies(data);
    } catch (err) {
      console.error('Error fetching companies:', err);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const onSubmit = async (data: UserFormData) => {
    try {
      const response = await authenticatedFetch(`${API_URL}/users`, {
        method: 'POST',
        body: JSON.stringify({ user: data })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify(errorData));
      }

      await response.json();
      navigate('/users');
    } catch (err) {
      console.error('Error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  return (
    <Container maxWidth="sm">
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            Create New User
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  type="email"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  fullWidth
                  margin="normal"
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Password"
                  type="password"
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  fullWidth
                  margin="normal"
                />
              )}
            />

            <Controller
              name="password_confirmation"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Password Confirmation"
                  type="password"
                  error={!!errors.password_confirmation}
                  helperText={errors.password_confirmation?.message}
                  fullWidth
                  margin="normal"
                />
              )}
            />

            <Controller
              name="roles"
              control={control}
              render={({ field }) => (
                <FormControl 
                  component="fieldset" 
                  sx={{ mt: 2, mb: 2 }}
                  error={!!errors.roles}
                >
                  <FormLabel component="legend">Roles</FormLabel>
                  <FormGroup>
                    {AVAILABLE_ROLES.map((role) => (
                      <FormControlLabel
                        key={role}
                        control={
                          <Checkbox
                            checked={field.value?.includes(role) || false}
                            onChange={(e) => {
                              const newRoles = e.target.checked
                                ? [...(field.value || []), role]
                                : (field.value || []).filter((r: string) => r !== role);
                              field.onChange(newRoles);
                            }}
                          />
                        }
                        label={getRoleDisplayName(role as Role)}
                      />
                    ))}
                  </FormGroup>
                  {errors.roles && (
                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                      {errors.roles.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />

            {isCompanyRepSelected && (
              <Controller
                name="company_id"
                control={control}
                render={({ field }) => (
                  <FormControl 
                    fullWidth 
                    margin="normal"
                    error={!!errors.company_id}
                  >
                    <InputLabel id="company-select-label">Company *</InputLabel>
                    <Select
                      {...field}
                      labelId="company-select-label"
                      label="Company *"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                      disabled={loadingCompanies}
                    >
                      {loadingCompanies ? (
                        <MenuItem value="" disabled>Loading companies...</MenuItem>
                      ) : companies.length === 0 ? (
                        <MenuItem value="" disabled>No companies available</MenuItem>
                      ) : (
                        companies.map((company) => (
                          <MenuItem key={company.id} value={company.id}>
                            {company.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    <FormHelperText>
                      {errors.company_id?.message || 'Select the company this representative belongs to'}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                fullWidth
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create User'}
              </Button>
              
              <Button 
                variant="outlined"
                fullWidth
                onClick={() => navigate('/users')}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default CreateUser;

